// MenuWizardScreen.Controls.tsx
import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { MenuRoleDTO, ProductForSaleDTO, Res, TicketLineDTO, TicketLineStatus, ValueButtonSize } from 'core/api';
import { Box, Flex, HStack, Link, Stack, Text, VStack, Button, Spinner, Span, Separator } from '@chakra-ui/react';
import { SvgIcon } from 'core/ui-next';
import { ProductButton, ProductButtonProps } from '../../sale/ProductButton';
import { PhoneOnly, ScrollableGrid, ThemeManager } from 'pos/ui';
import { buildDetailBlocks } from '../compositionPanelDetailsLine';

type OnProductSelected = (product: ProductForSaleDTO, role: MenuRoleDTO) => Promise<void>;

export function getScrollTargetId(item: MenuRoleDTO | TicketLineDTO) {
	if ('Id' in item) return `line-${item.Id}`;
	return `role-${item.Name}`;
}

function buildProductButtons(
	role: MenuRoleDTO,
	productsByRole: Record<string, ProductForSaleDTO[]>,
	buttonSize: ValueButtonSize,
	onProductSelected: OnProductSelected,
	getQuantity: (product: ProductForSaleDTO, role: MenuRoleDTO) => number,
	keyScope = role.Name
) {
	const largeDeviceSizes: Record<ValueButtonSize, ProductButtonProps<any>['size']> = {
		[ValueButtonSize.Small]: 'sm',
		[ValueButtonSize.Medium]: 'md',
		[ValueButtonSize.Large]: 'lg'
	};

	const smallDeviceSizes: Record<ValueButtonSize, ProductButtonProps<any>['size']> = {
		[ValueButtonSize.Small]: 'phone-md',
		[ValueButtonSize.Medium]: 'phone-md',
		[ValueButtonSize.Large]: 'phone-lg'
	};

	const size = ThemeManager.isPhone() ? smallDeviceSizes[buttonSize] : largeDeviceSizes[buttonSize];
	const products = productsByRole[role.Name] ?? [];


	return {
		size,
		items: products.map(p => (
			<ProductButton
				key={`${keyScope}.${p.SaleFormatId}`}
				value={p}
				disabled={!p.IsAvailable}
				onClick={p => onProductSelected(p, role)}
				size={size}
				quantity={getQuantity(p, role)}
			/>
		)),
	};
}

function getLineStatusIcon(line: TicketLineDTO): React.ReactNode {
	const statusImage = line.HasBeenRequested
		? Images.LineStatusSent
		: line.Status === TicketLineStatus.New
			? Images.LineStatusCommand
			: Images.LineStatusMarch;

	return <SvgIcon src={statusImage} h="3" w="3" />;
}

type CompositionLineCardProps = {
	line: TicketLineDTO;
	selectedLine: Nullable<TicketLineDTO>;
	onSelect: (line: TicketLineDTO) => void;
	onEdit: (line: TicketLineDTO) => void;
	onDelete: (line: TicketLineDTO) => void;
};

function getMenuLineDetailBlocks(line: TicketLineDTO): React.ReactNode | null {

	return buildDetailBlocks({
		size: line.Product.Size,
		color: line.Product.Color,
		addins: line.Addins,
		removedIngredients: line.RemovedIngredients,
		lot: line.Product.Lot,
		notes: line.Notes,
		isProductComposition: false,
	});
}

const CompositionLineCard = React.forwardRef<HTMLDivElement, CompositionLineCardProps>(
	({ line, onSelect, selectedLine, onEdit, onDelete }, ref) => {
		const isSelected = line.Id === selectedLine?.Id;
		const [showControlButtons, setShowControlButtons] = React.useState(false);

		React.useEffect(() => {
			if (!isSelected)
				setShowControlButtons(false);
		}, [isSelected]);

		const onControlLine = () => {

			if (showControlButtons) {
				setShowControlButtons(false);
				return;
			}

			onSelect(line);
			setShowControlButtons(true);
		};

		return (
			<Stack
				id={getScrollTargetId(line)}
				ref={ref}
				w="full"
				p="2"
				gap="2"
				border="1px solid"
				borderRadius="md"
				cursor="pointer"
				lineHeight="120%"
				onClick={onControlLine}
				bg={{
					base: isSelected ? 'blue.50' : 'yellow.50',
					_dark: isSelected ? 'blue.700' : 'neutral.700',
				}}
				borderColor={{
					base: isSelected ? 'blue.200' : 'yellow.50',
					_dark: isSelected ? 'blue.700' : 'neutral.700',
				}}
				color={{ base: isSelected ? 'neutral.800' : 'neutral.600', _dark: 'white' }}
				_hover={{ borderColor: { base: 'blue.200', _dark: 'blue.700' } }}
			>
				<Flex justify="space-between" align="center" minH="6">
					<HStack gap="2" align="center" flex="1" minW="0">
						<Box w="3" flex="0 0 auto">
							{getLineStatusIcon(line)}
						</Box>

						<Text
							minW="11"
							fontSize="sm"
							fontWeight="semibold"
							textAlign="right"
							flex="0 0 auto"
						>
							{UI.Format.asShortQuantity(line.Quantity)}
						</Text>

						<Text fontSize="sm" lineClamp={1} flex="1">
							{line.Product.SaleFormatName}
						</Text>
					</HStack>

					{isSelected && showControlButtons && (
						<HStack gap="2" flex="0 0 auto" height={'0'}>
							<Box
								as="button"
								aria-label="Eliminar"
								w="8"
								h="8"
								borderRadius="md"
								bg={{ base: 'red.700', _dark: 'red.400' }}
								color="white"
								display="grid"
								placeItems="center"
								cursor={'pointer'}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									onDelete(line);
								}}
							>
								<SvgIcon src={Images.TrashCan} w="4" h="4" />
							</Box>

							<Box
								as="button"
								aria-label="Editar"
								w="14"
								h="8"
								borderRadius="md"
								bg="primary"
								color="white"
								display="grid"
								placeItems="center"
								cursor={'pointer'}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									onEdit(line);
								}}
							>
								<SvgIcon src={Images.Edit} w="4" h="4" />
							</Box>
						</HStack>
					)}
				</Flex>

				{getMenuLineDetailBlocks(line)}
			</Stack>
		);
	}
);

type RoleSectionProps = {
	role: MenuRoleDTO;
	selectedRoleName: string;
	lines: TicketLineDTO[];
	selectedLine: Nullable<TicketLineDTO>;
	menuQty: number;
	showRoleHeader: boolean;
	onSelectLine: (line: TicketLineDTO) => void;
	onEditLine: (line: TicketLineDTO) => void;
	onDeleteLine: (line: TicketLineDTO) => void;
};

const RoleSection = React.forwardRef<HTMLDivElement, RoleSectionProps>(
	(
		{
			role,
			lines,
			selectedRoleName,
			selectedLine,
			onSelectLine,
			onEditLine,
			onDeleteLine,
			menuQty,
			showRoleHeader,
		},
		_ref
	) => {
		const selectedCount = lines.sum(x => x.Quantity);
		const maxCount = menuQty * role.MaxItems;

		return (
			<VStack w="full" align="stretch" gap="2">
				{showRoleHeader && (
					<HStack id={getScrollTargetId(role)} w="full" justify="space-between" align="baseline">
						<Text fontSize="sm" fontWeight={selectedRoleName === role.Name ? 'bold' : 'normal'}>
							{role.Name}
						</Text>
						<Text fontSize="xs" fontWeight={'semibold'} color={selectedCount === maxCount ? 'success' : 'warning'}>
							{Res.Web.COMPOSE_MENU_ROLE_SELECTION_COUNT_LBL(selectedCount, maxCount)}
						</Text>
					</HStack>
				)}

				<VStack w="full" align="stretch" gap="2">
					{lines.map(line => (
						<CompositionLineCard
							key={line.Id}
							line={line}
							selectedLine={selectedLine}
							onSelect={onSelectLine}
							onEdit={onEditLine}
							onDelete={onDeleteLine}
						/>
					))}
				</VStack>
			</VStack>
		);
	}
);

export type CompositionPanelProps = {
	roles: MenuRoleDTO[];
	selectedRole: MenuRoleDTO;
	header: TicketLineDTO;
	lines: TicketLineDTO[];
	selectedLine: Nullable<TicketLineDTO>;
	onEditLine: (line: TicketLineDTO) => void;
	onDeleteLine: (line: TicketLineDTO) => void;
	onSelectLine: (line: TicketLineDTO) => void;
	width?: string;
};

export function CompositionPanel({
	roles,
	selectedRole,
	header,
	lines,
	onDeleteLine,
	onEditLine,
	onSelectLine,
	selectedLine,
	width = '500px',
}: CompositionPanelProps) {
	const [showRoleHeaders, setShowRoleHeaders] = React.useState(true);

	return (
		<VStack
			width={width}
			height="full"
			overflow="auto"
			align="stretch"
			gap={ThemeManager.isLargeDevice() ? '6' : '4'}
			padding={'1.5'}
		>
			{/* Header */}
			<HStack w="full" gap="2" align="center">
				<Text fontWeight="semibold">{header.Product.SaleFormatName}</Text>
				<SvgIcon h="4" w="4" src={Images.TableCompanions} />
				<Text>{UI.Format.asShortQuantity(header.Quantity)}</Text>
			</HStack>

			<PhoneOnly>
				<Separator width={'full'} orientation={'horizontal'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} height={'1.25px'} />
			</PhoneOnly>

			<VStack width={'full'} height={'full'} gap={'2'}>

				{/* Toggle details */}
				<HStack w="full" justify="flex-end">
					<Link onClick={() => setShowRoleHeaders(v => !v)} fontSize={'xs'} color={'info'}>
						{showRoleHeaders ? Res.Web.COMPOSE_MENU_HIDE_MORE_DETAILS() : Res.Web.COMPOSE_MENU_SHOW_MORE_DETAILS()}
					</Link>
				</HStack>

				{/* Roles */}
				<VStack w="full" align="stretch" gap="4">
					{roles.map(role => (
						<RoleSection
							key={role.Name}
							menuQty={header.Quantity}
							role={role}
							selectedRoleName={selectedRole.Name}
							lines={lines.filter(x => x.Product.GroupName === role.Name)}
							selectedLine={selectedLine}
							onDeleteLine={onDeleteLine}
							onEditLine={onEditLine}
							onSelectLine={onSelectLine}
							showRoleHeader={showRoleHeaders}
						/>
					))}
				</VStack>
			</VStack>
		</VStack>
	);
}

export function RoleTabs({
	roles,
	currentRoleName,
	onRoleSelected,
}: {
	roles: MenuRoleDTO[];
	currentRoleName: string;
	onRoleSelected: (role: MenuRoleDTO) => void;
}) {

	const stepsContainerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (stepsContainerRef.current) {
			const selected = stepsContainerRef.current.querySelector('[data-selected="true"]');
			selected?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
		}
	}, [currentRoleName]);

	return (
		<HStack
			padding="2"
			gap={'0'}
			width={'full'}
			overflowX={'auto'}
			overflowY={'hidden'}
			flexShrink={0}
			ref={stepsContainerRef}
		>
			{roles.map((r, index) => (
				<Button
					key={r.Name}
					data-selected={currentRoleName === r.Name}
					onClick={() => onRoleSelected(r)}
					bg={currentRoleName === r.Name ? { base: 'yellow.500', _dark: 'orange.500' } : 'transparent'}
					color={'neutral.800'}
					maxH={currentRoleName === r.Name ? '10' : '9'}
					minW={'36'}
					display={'flex'}
					gap={'0.5'}
					alignItems={'center'}
					justifyContent={'center'}
					overflow={'visible'}
					isolation={'isolate'}
					opacity={currentRoleName === r.Name ? 1 : 0.6}
					_after={{
						content: '" "',
						position: 'absolute',
						inset: '-2px',
						bg: currentRoleName === r.Name ? { base: 'yellow.300', _dark: 'yellow.400' } : 'yellow.200',
						clipPath: {
							base: index === 0 ? 'polygon(0% 0%, calc(100% - 11px) 0%, 99.5% 50%, calc(100% - 11px) 100%, 0% 100%)' :
								index === roles.length - 1 ? 'polygon(1% 0%, 100% 0%, 100% 100%, 1% 100%, 12px 50%)' :
									'polygon(1% 0%, calc(100% - 11.5px) 0%, 99.5% 50%, calc(100% - 11.5px) 100%, 1% 100%, 12px 50%)'
						},
						zIndex: -1,
						margin: '3px',
						borderRadius: '3px'
					}}
					clipPath={
						index === 0 ? 'polygon(0% 0%, calc(100% - 11.5px) 0%, 100% 50%, calc(100% - 11.5px) 100%, 0% 100%)' :
							index === roles.length - 1 ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 12px 50%)' :
								'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)'
					}
				>
					<Text>{r.Name}</Text>
				</Button>
			))}
		</HStack>
	);
}


export function CurrentRoleProducts({
	role,
	productsByRoleName,
	buttonSize,
	onProductSelected,
	lines,
}: {
	role: MenuRoleDTO;
	productsByRoleName: Record<string, ProductForSaleDTO[]>
	buttonSize: ValueButtonSize;
	onProductSelected: OnProductSelected;
	lines: TicketLineDTO[];
}) {

	const getQuantity = (p: ProductForSaleDTO) => lines.filter(l => l.Product.SaleFormatId === p.SaleFormatId && l.Product.GroupName === role.Name).sum(l => l.Quantity);
	// ProductButton memoiza ignorando el onClick; al cargar más roles en cache, fuerzo remount para no conservar un handler con composition antigua.
	// La salida más normal es hacer que el ProductButton no meomizara onClick, pero me da miedo cambiarlo por temas de rendimiento. Como de momento
	// sólo afecta a esta parte y en el resto de la aplicación parece que no ha dado problemas, meto este HACK.
	const roleKey = `${role.Name}.${Object.keys(productsByRoleName).length}`;
	const { items, size } = buildProductButtons(role, productsByRoleName, buttonSize, onProductSelected, getQuantity, roleKey);

	return (
		<Box width="full" flexShrink={0}>
			<ScrollableGrid items={items} buttonSize={size} />
		</Box>
	);
}

export function OtherRolesOptions({
	expanded,
	loading,
	currentRole,
	roles,
	productsByRoleName,
	buttonSize,
	onToggle,
	onProductSelected,
	lines
}: {
	expanded: boolean;
	loading: boolean;
	currentRole: MenuRoleDTO,
	roles: MenuRoleDTO[];
	productsByRoleName: Record<string, ProductForSaleDTO[]>;
	buttonSize: ValueButtonSize;
	onToggle: () => void;
	onProductSelected: OnProductSelected;
	lines: TicketLineDTO[];
}) {

	const rolesWithSameCategory = roles.groupBy(x => x.Category.Id).filter(x => x.value.length > 1).flatMap(x => x.value) ?? [];
	const repeatedCandidateRole = rolesWithSameCategory.length ? rolesWithSameCategory[0] : null;
	const repeatedRoleProducts = repeatedCandidateRole !== null && productsByRoleName[repeatedCandidateRole.Name] != null ? productsByRoleName[repeatedCandidateRole.Name].map(x => x.SaleFormatId) : [];

	const currentSaleFormatIds = productsByRoleName[currentRole.Name].map(x => x.SaleFormatId);
	const rolesWithSameCategoryNames = new Set(rolesWithSameCategory.map(x => x.Name));
	const currentSaleFormatIdSet = new Set(currentSaleFormatIds);
	const repeatedRoleProductSet = new Set(repeatedRoleProducts);

	const getQuantity = (p: ProductForSaleDTO, role: MenuRoleDTO) => {

		// Si el producto existe en el role actual, su contador se pinta sólo en CurrentRoleProducts.
		// En OtherRolesOptions debe salir a 0 para no duplicar el badge.
		const isInCurrentRole = currentSaleFormatIdSet.has(p.SaleFormatId);
		if (isInCurrentRole)
			return 0;

		// La cantidad SIEMPRE se cuenta contra las líneas que ya están en el role actual
		// (mismo SaleFormatId + GroupName del currentRole).
		// Luego las siguientes reglas sólo deciden si se muestra ese total o se oculta (0).
		const quantityInCurrentRole = lines
			.filter(l => l.Product.SaleFormatId === p.SaleFormatId && currentRole.Name === l.Product.GroupName)
			.sum(l => l.Quantity);

		// Si hay roles con categoría repetida, mostramos los productos repetidos sólo en un role "candidato"
		// (el primero detectado) para evitar duplicar visualmente los mismos botones con cantidad.
		const isRepeatedProduct = repeatedRoleProductSet.has(p.SaleFormatId);
		const isRepeatedCandidateRole = role.Name === repeatedCandidateRole?.Name;
		if (isRepeatedProduct && isRepeatedCandidateRole)
			return quantityInCurrentRole;

		// Si este role pertenece a una categoría repetida pero NO es el role candidato,
		// ocultamos el contador (0).
		const isRoleWithRepeatedCategory = rolesWithSameCategoryNames.has(role.Name);
		if (isRoleWithRepeatedCategory && !isRepeatedCandidateRole)
			return 0;

		// Para roles no repetidos, mostramos la cantidad acumulada en el role actual.
		return quantityInCurrentRole;
	};

	return (
		<VStack width="full" flexShrink={0} gap={'2'}>
			<Link alignSelf="end" onClick={onToggle} color={'info'}>
				<Span display={'flex'} alignItems={'center'} gap={'1'}>
					<Text>{expanded ? Res.Web.COMPOSE_MENU_HIDE_MORE_OPTIONS() : Res.Web.COMPOSE_MENU_SHOW_MORE_OPTIONS()}</Text>
					<SvgIcon src={expanded ? Images.ChevronUp : Images.ChevronDown} w="4" h="4" />
				</Span>
			</Link>

			{expanded && loading ? <Spinner /> : null}

			{expanded && !loading
				? <VStack width={'full'} align="stretch" gap={'4'}>
					{roles.map(role => {

						const { size, items } = buildProductButtons(
							role,
							productsByRoleName,
							buttonSize,
							onProductSelected,
							getQuantity,
							role.Name
						);

						return <VStack alignItems="start" key={role.Name} width="full" gap={'4'}>
							<Text>{role.Name}</Text>
							<ScrollableGrid items={items} buttonSize={size} />
						</VStack>;

					})}
				</VStack>
				: null}
		</VStack>
	);
}
